/**
 * Представление таблицы сравнения производств — МСК Газобетон.
 *
 * Своих цен, доставки и сортировки здесь нет. На вход приходит уже собранный и
 * отсортированный список сравнений (sortedGroupedData), на выход — то же самое
 * в том же порядке, но с посчитанной суммой к оплате и разницей относительно
 * самого дешёвого варианта.
 *
 * Сумма к оплате берётся той же функцией, что и основной расчёт заказа
 * (resolveGazCashTotal): при наличных — с округлением до 500 ₽, при безналичных —
 * без него. Второй формулы округления не заводится, поэтому итог выбранной строки
 * совпадает с итогом наверху экрана.
 *
 * Сравнение строится только по строкам «Товаров»: допродажи в него не попадают
 * (см. BackendProductions — сравнение считается по productsForCalc). Поэтому
 * разница здесь — это разница по газобетону и доставке, а не экономия по заказу.
 */

import { resolveGazCashTotal } from '../Offer/gazCashRounding.js';
import { getGazUnloadingSum } from './gazTotalBreakdown.js';
import { daysWord } from '../../utils/gazDeclensions.js';

// Значения, которые в данных означают «даты нет».
const EMPTY_DATE_VALUES = new Set(['', '-', '—', 'Нет данных']);

// Второй сорт: плотности вперемешку, возможны сколы, товар невозвратный. Сорт
// записан только в названии номенклатуры, отдельного поля под него нет.
const VTOROY_SORT_MARKER = '2 сорт';

/**
 * Строка предлагает второй сорт.
 *
 * Считается по фактической номенклатуре строки — тому, что производство реально
 * отдаёт по этому расчёту. Если в строке несколько позиций и хоть одна из них
 * второго сорта, строка считается второсортной: смешанную поставку менеджер
 * должен видеть с пометкой, а не без неё.
 */
export const isVtoroySortComparisonRow = (nomenclature) => (
  (Array.isArray(nomenclature) ? nomenclature : [nomenclature])
    .some((name) => String(name || '').toLowerCase().includes(VTOROY_SORT_MARKER))
);

/** Дата обновления цены строки прайса, если она там действительно есть. */
const priceDateOf = (material) => {
  const value = String(material?.date ?? '').trim();
  return EMPTY_DATE_VALUES.has(value) ? null : value;
};

/**
 * Возраст цены в днях по дате из прайса.
 *
 * Дата приходит строкой «20.08.2026». Считаем в календарных сутках: и дату, и
 * сегодняшний день приводим к полуночи UTC, поэтому «вчера в 23:50» и «вчера в
 * 00:10» одинаково дают один день, а часовой пояс на результат не влияет.
 *
 * @param {string} date — «20.08.2026»
 * @param {Date}   [now] — текущий момент, отдельным аргументом ради тестов
 * @returns {number|null} возраст в днях либо null, если даты нет или она битая
 */
export const getGazPriceAgeDays = (date, now = new Date()) => {
  const match = String(date ?? '').trim().match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
  if (!match) {
    return null;
  }

  const day = Number(match[1]);
  const month = Number(match[2]);
  const year = Number(match[3]);
  const priceDay = Date.UTC(year, month - 1, day);
  const parsed = new Date(priceDay);
  // 31.02.2026 разберётся регуляркой, но датой не является.
  if (
    parsed.getUTCFullYear() !== year
    || parsed.getUTCMonth() !== month - 1
    || parsed.getUTCDate() !== day
  ) {
    return null;
  }

  const today = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
  const diff = Math.round((today - priceDay) / 86400000);
  // Дата из будущего — это ошибка данных, а не «минус два дня назад».
  return Number.isFinite(diff) && diff >= 0 ? diff : null;
};

/**
 * Возраст цены словами: «сегодня», «вчера», «2 дня назад», «5 дней назад».
 *
 * Первый день назван отдельным словом: «1 день назад» по-русски так не говорят.
 * Дальше работает общее склонение из gazDeclensions — своей таблицы форм здесь нет.
 */
export const formatGazPriceAge = (days) => {
  // null, undefined и пустая строка — это «возраста нет», а не ноль дней:
  // Number(null) === 0 молча превратил бы отсутствие даты в «сегодня».
  if (days === null || days === undefined || days === '') {
    return null;
  }
  const value = Number(days);
  if (!Number.isFinite(value) || value < 0) {
    return null;
  }

  const whole = Math.round(value);
  if (whole === 0) {
    return 'сегодня';
  }
  if (whole === 1) {
    return 'вчера';
  }
  return `${whole} ${daysWord(whole)} назад`;
};

/**
 * Как показать актуальность цены.
 *
 * Менеджеру важно не число в календаре, а насколько цена свежая, поэтому дата из
 * данных (PRICE_UPDATE_DATE) переводится в возраст словами. Саму дату и правило её
 * определения это не меняет: считаем по тому, что уже пришло в строке.
 *
 * @param {string|null} date — «20.08.2026» или null
 * @param {number|null} days — возраст в днях, если в данных есть только он
 * @param {Date}        [now] — текущий момент, отдельным аргументом ради тестов
 */
export const formatGazPriceFreshness = ({ date, days, now } = {}) => {
  // Готовый возраст в днях, если он уже посчитан до нас.
  if (days !== null && days !== undefined && days !== '') {
    const ready = Number(days);
    if (Number.isFinite(ready) && ready >= 0) {
      return formatGazPriceAge(ready);
    }
  }

  const exact = String(date ?? '').trim();
  if (!exact || EMPTY_DATE_VALUES.has(exact)) {
    return null;
  }
  return formatGazPriceAge(getGazPriceAgeDays(exact, now || new Date()));
};

/**
 * Объём газобетона строки в кубометрах.
 *
 * Ничего не пересчитывает: берёт фактический объём позиций, который уже посчитан
 * в BackendProductions (factVolume у газобетонных строк равен факт. количеству,
 * у остальных там «-»). Запасной путь — количество, когда базовая единица уже м³.
 * У разных производств объём может отличаться: округление до полного поддона
 * зависит от размера блока, поэтому объём берётся у каждой строки свой.
 */
export const getGazRowVolumeM3 = (entry) => {
  const materials = Array.isArray(entry?.materialsToShow) ? entry.materialsToShow : [];

  const volume = materials.reduce((sum, material) => {
    const fact = Number(material?.factVolume);
    if (Number.isFinite(fact) && fact > 0) {
      return sum + fact;
    }
    const unit = String(material?.unit ?? '').trim().toLowerCase().replace('³', '3');
    if (unit !== 'м3') {
      return sum;
    }
    const quantity = Number(material?.quantity);
    return Number.isFinite(quantity) && quantity > 0 ? sum + quantity : sum;
  }, 0);

  return volume > 0 ? volume : null;
};

/**
 * Цена за кубометр с доставкой.
 *
 * Та же формула, по которой калькулятор уже печатает цену за куб в блоках КП
 * (см. buildPriceSummaries: allocatedSum / volume, где allocatedSum — доля строки
 * в сумме блока вместе с доставкой). Здесь она берётся целиком по строке: сумма к
 * оплате, которая уже показана в колонке «К оплате», делится на объём этой же
 * строки. Своего расчёта цен, доставки и тарифов не появляется.
 */
export const getGazPricePerM3 = ({ payableTotal, volumeM3 } = {}) => {
  const total = Number(payableTotal);
  const volume = Number(volumeM3);
  if (!Number.isFinite(total) || total <= 0 || !Number.isFinite(volume) || volume <= 0) {
    return null;
  }
  return total / volume;
};

/** Источники цен строки — как они пришли, без домысливания. */
const sourcesOf = (entry) => Array.from(new Set(
  (Array.isArray(entry?.materialsToShow) ? entry.materialsToShow : [])
    .map((material) => String(material?.source ?? '').trim())
    .filter((source) => source && source !== '-')
));

/**
 * Сумма, которую менеджер назовёт клиенту по этой строке.
 *
 * Наличные — округление до 500 ₽ существующей функцией. Безналичные — та же
 * сумма без округления; вверх до рубля, как её и печатает formatMoney.
 * null — итога нет (нет данных или ошибка расчёта доставки).
 */
export const getGazPayableTotal = ({ total, isMskGazobetonScope, paymentMethod } = {}) => {
  const resolved = resolveGazCashTotal({ total, isMskGazobetonScope, paymentMethod });
  if (resolved.total === null || !(resolved.total > 0)) {
    return null;
  }
  return resolved.applies ? resolved.total : Math.ceil(resolved.total);
};

/**
 * Разница строки относительно самого дешёвого варианта.
 *
 * Считается по фактически показанным суммам: при наличных — по округлённым,
 * при безналичных — по точным. Если после округления итоги совпали, экономии нет
 * и придумывать её нельзя.
 *
 * @returns {{kind:'min'|'same'|'more'|'none', amount:number}}
 */
export const getGazTotalDifference = ({ payableTotal, bestTotal, isBest } = {}) => {
  if (payableTotal === null || payableTotal === undefined || bestTotal === null || bestTotal === undefined) {
    return { kind: 'none', amount: 0 };
  }
  if (isBest) {
    return { kind: 'min', amount: 0 };
  }
  const amount = payableTotal - bestTotal;
  if (amount === 0) {
    return { kind: 'same', amount: 0 };
  }
  // Дешевле самого дешёвого бывает только второй сорт: метка минимальной цены
  // считается по обычным блокам, а второсортная строка стоит ниже их всех. Писать
  // ей «та же цена» нельзя — разницу надо назвать (13.10.2026, Роман).
  if (amount < 0) {
    return { kind: 'less', amount: -amount };
  }
  return { kind: 'more', amount };
};

/* --- Сортировка по числовым колонкам --- */

export const GAZ_SORT_ASC = 'asc';
export const GAZ_SORT_DESC = 'desc';

/**
 * Колонки, по которым таблица умеет сортироваться.
 *
 * Значения — имена полей строки представления, поэтому сортировка идёт по тем же
 * числам, что и показаны: payableTotal — это итог, по которому уже определяется
 * метка минимальной цены, второй суммы для сортировки не заводится.
 */
export const GAZ_SORT_BY_TOTAL = 'payableTotal';
export const GAZ_SORT_BY_PRICE_PER_M3 = 'pricePerM3';

const SORTABLE_KEYS = new Set([GAZ_SORT_BY_TOTAL, GAZ_SORT_BY_PRICE_PER_M3]);

/**
 * Следующее состояние сортировки по нажатию на заголовок.
 *
 * Первое нажатие — от меньшей цены к большей, повторное — наоборот, третье
 * возвращает порядок самого калькулятора (по итоговой сумме). Возврат нужен:
 * иначе к исходному порядку не вернуться, не перезагрузив расчёт.
 */
export const nextGazSortDirection = (current) => {
  if (current === GAZ_SORT_ASC) {
    return GAZ_SORT_DESC;
  }
  if (current === GAZ_SORT_DESC) {
    return null;
  }
  return GAZ_SORT_ASC;
};

/**
 * Строки, переставленные по выбранной числовой колонке.
 *
 * Только переставляет: суммы, метки и признак выбора остаются у своих строк, ни
 * одна строка не теряется и не дублируется. Строки без значения (нет объёма, нет
 * итога или ошибка расчёта доставки) всегда уходят вниз — и при возрастании, и при
 * убывании: пустое значение не должно возглавлять список «самых дешёвых».
 *
 * @param {Array}       rows      — строки представления
 * @param {string|null} key       — 'payableTotal' или 'pricePerM3'
 * @param {string|null} direction — 'asc', 'desc' или null (порядок калькулятора)
 */
export const sortGazComparisonRows = ({ rows, key, direction } = {}) => {
  const list = Array.isArray(rows) ? rows : [];
  if (!SORTABLE_KEYS.has(key)) {
    return list;
  }
  if (direction !== GAZ_SORT_ASC && direction !== GAZ_SORT_DESC) {
    return list;
  }

  const sign = direction === GAZ_SORT_ASC ? 1 : -1;

  // Сортировка в JS устойчивая (ES2019), поэтому строки с одинаковым значением
  // сохраняют порядок калькулятора.
  return [...list].sort((left, right) => {
    // Второй сорт держится внизу при любой сортировке: он дешевле, и по цене
    // возглавил бы список, подменив собой обычный блок (13.10.2026, Роман).
    const leftVtoroy = left?.isVtoroySort ? 1 : 0;
    const rightVtoroy = right?.isVtoroySort ? 1 : 0;
    if (leftVtoroy !== rightVtoroy) {
      return leftVtoroy - rightVtoroy;
    }

    const a = Number(left?.[key]);
    const b = Number(right?.[key]);
    const aOk = Number.isFinite(a) && a > 0;
    const bOk = Number.isFinite(b) && b > 0;

    if (!aOk && !bOk) {
      return 0;
    }
    if (!aOk) {
      return 1;
    }
    if (!bOk) {
      return -1;
    }
    return (a - b) * sign;
  });
};

/* --- Выбор производства нажатием на строку --- */

export const GAZ_ROW_ACTIVATION_KEYS = Object.freeze(['Enter', ' ', 'Spacebar']);

const INTERACTIVE_SELECTOR = 'a, button, input, select, textarea, [role="button"], [contenteditable="true"]';

/**
 * Нажали по вложенному интерактивному элементу, а не по самой строке.
 *
 * Сама строка носителем события не считается: у неё role="button", и без этой
 * оговорки любой клик по строке выглядел бы как клик по кнопке.
 */
export const isInsideInteractiveElement = (event) => {
  const target = event?.target;
  if (!target || typeof target.closest !== 'function') {
    return false;
  }
  const interactive = target.closest(INTERACTIVE_SELECTOR);
  return Boolean(interactive) && interactive !== event.currentTarget;
};

/**
 * Выбирать ли производство по этому нажатию.
 *
 * @param {string}  [key]              — клавиша; для мыши не передаётся
 * @param {boolean} isSelected         — строка уже выбрана
 * @param {boolean} insideInteractive  — нажали по вложенному элементу
 */
export const shouldActivateComparisonRow = ({ key, isSelected, insideInteractive } = {}) => {
  if (insideInteractive) {
    return false;
  }
  // Повторный выбор уже выбранной строки только зря пересобирает предложение.
  if (isSelected) {
    return false;
  }
  if (key === undefined || key === null) {
    return true;
  }
  return GAZ_ROW_ACTIVATION_KEYS.includes(key);
};

/**
 * Строки таблицы сравнения в том же порядке, в каком их отдала сортировка.
 *
 * Выбранная строка наверх не поднимается: менеджер должен видеть её настоящее
 * место среди вариантов. Самый дешёвый вариант только помечается — производство
 * автоматически не переключается.
 *
 * @param {object}  params
 * @param {Array}   params.rows                 — sortedGroupedData как есть
 * @param {string}  params.paymentMethod        — форма оплаты
 * @param {*}       params.selectedComparisonId — ключ выбранного производства
 * @param {boolean} params.isMskGazobetonScope  — признак области
 * @param {Date}    [params.now]                — текущий момент для возраста цены
 */
export const buildGazComparisonView = ({
  rows,
  paymentMethod,
  selectedComparisonId,
  isMskGazobetonScope,
  now,
} = {}) => {
  const list = Array.isArray(rows) ? rows : [];

  if (!isMskGazobetonScope || list.length === 0) {
    return { rows: [], bestId: null, commonSource: null, isCashRounded: false };
  }

  const prepared = list.map((entry) => {
    const rawTotal = Number(entry?.totalSum);
    const payableTotal = getGazPayableTotal({
      total: entry?.totalSum,
      isMskGazobetonScope,
      paymentMethod,
    });

    const volumeM3 = getGazRowVolumeM3(entry);

    // Разгрузка сидит внутри доставки, а значит и внутри суммы к оплате. В цену
    // куба она не входит — так же, как в одноимённом показателе верхней сводки,
    // поэтому вычитается из той же показанной суммы. Своего расчёта нет: сумма
    // разгрузки пришла с сервера готовой.
    const unloadingSum = getGazUnloadingSum(entry);
    const pricePerM3Total = payableTotal === null ? null : payableTotal - unloadingSum;

    return {
      entry,
      id: entry?.id ?? null,
      comparison: entry?.comparison ?? null,
      volumeM3,
      // Цена за куб считается по уже показанной сумме к оплате без разгрузки: при
      // наличных — по округлённой, как её и назовут клиенту.
      pricePerM3: getGazPricePerM3({ payableTotal: pricePerM3Total, volumeM3 }),
      production: String(entry?.comparison?.production || '').trim(),
      materialsTotal: Number(entry?.totalMaterialCostForDisplay) || 0,
      deliveryTotal: Number(entry?.totalDeliveryCost) || 0,
      rawTotal: Number.isFinite(rawTotal) ? rawTotal : null,
      payableTotal,
      hasDeliveryError: Boolean(entry?.hasDeliveryError),
      deliveryError: entry?.deliveryError || null,
      // Фактическая номенклатура строки: то, что производство реально предлагает
      // и по чему посчитана его сумма. Берётся из готового materialsToShow.
      nomenclature: Array.from(new Set(
        (Array.isArray(entry?.materialsToShow) ? entry.materialsToShow : [])
          .map((material) => String(material?.name || '').trim())
          .filter(Boolean)
      )),
      // Актуальность цены. Даты разных позиций строки могут отличаться — тогда так
      // и пишем, а не выбираем одну из них наугад.
      priceDates: Array.from(new Set(
        (Array.isArray(entry?.materialsToShow) ? entry.materialsToShow : [])
          .map(priceDateOf)
          .filter(Boolean)
      )),
      sources: sourcesOf(entry),
      isVtoroySort: isVtoroySortComparisonRow(
        (Array.isArray(entry?.materialsToShow) ? entry.materialsToShow : [])
          .map((material) => String(material?.name || '').trim())
      ),
      isSelected: Boolean(
        selectedComparisonId !== null
        && selectedComparisonId !== undefined
        && entry?.comparison?.id === selectedComparisonId
      ),
    };
  });

  // Второй сорт идёт отдельной группой внизу: он дешевле обычного блока, и в общем
  // порядке встал бы выше, выглядя как тот же товар подешевле (13.10.2026, Роман).
  // Порядок внутри каждой группы — тот, что пришёл из калькулятора.
  const pervySort = prepared.filter((row) => !row.isVtoroySort);
  const vtoroySort = prepared.filter((row) => row.isVtoroySort);
  const uporyadochennye = [...pervySort, ...vtoroySort];

  // Самый дешёвый — по показанной сумме. При равных показанных суммах берётся
  // меньшая исходная: это только устойчивость метки, ручной выбор не трогается.
  // Метка считается по обычным блокам: второй сорт дешевле всегда, и «минимальная
  // цена» на нём означала бы не выгодный завод, а другой товар. Если весь расчёт
  // идёт по второму сорту, метка считается по нему же.
  const dlyaMetki = pervySort.length > 0 ? pervySort : vtoroySort;
  const best = dlyaMetki.reduce((chosen, row) => {
    if (row.payableTotal === null) {
      return chosen;
    }
    if (!chosen) {
      return row;
    }
    if (row.payableTotal !== chosen.payableTotal) {
      return row.payableTotal < chosen.payableTotal ? row : chosen;
    }
    const rowRaw = row.rawTotal ?? Number.POSITIVE_INFINITY;
    const chosenRaw = chosen.rawTotal ?? Number.POSITIVE_INFINITY;
    return rowRaw < chosenRaw ? row : chosen;
  }, null);

  const bestTotal = best ? best.payableTotal : null;

  // Источник у всех строк один — выносим его над таблицей и не повторяем в каждой
  // строке. Если источники разные, они остаются у своих строк: скрывать различие
  // нельзя.
  const allSources = Array.from(new Set(prepared.flatMap((row) => row.sources)));
  const commonSource = allSources.length === 1 ? allSources[0] : null;

  return {
    bestId: best ? best.id : null,
    commonSource,
    // Наличное округление применено — подпись колонки говорит об этом словами.
    isCashRounded: resolveGazCashTotal({
      total: 1,
      isMskGazobetonScope,
      paymentMethod,
    }).applies,
    rows: uporyadochennye.map((row) => {
      const isBest = Boolean(best && row.id === best.id);
      return {
        ...row,
        isBest,
        // Одинаковый источник у всех строк показывается один раз над таблицей.
        rowSources: commonSource ? [] : row.sources,
        priceFreshness: row.priceDates.length === 1
          ? formatGazPriceFreshness({ date: row.priceDates[0], now })
          : (row.priceDates.length > 1 ? 'Разное' : null),
        difference: getGazTotalDifference({
          payableTotal: row.payableTotal,
          bestTotal,
          isBest,
        }),
      };
    }),
  };
};

export default buildGazComparisonView;
